/// <amd-module name="VSSUI/Components/Expandable/Expandable" />
import * as React from "react";
import { FocusGroupContext } from "VSSUI/FocusGroup";
import { FocusZoneContext } from "VSSUI/FocusZone";
import { IMouseWithinStatus, MouseWithin } from "VSSUI/MouseWithin";
import { css, getSafeId, KeyCode, setFocusVisible } from "VSSUI/Util";
import { getTabIndex } from "VSSUI/Utilities/Focus";
import { IExpandable, IExpandableChildProps, IExpandableContainerProps, IExpandableProps } from "./Expandable.Props";

export interface IExpandableState {
    expanded: boolean;
}

export class Expandable extends React.Component<IExpandableProps, IExpandableState> implements IExpandable {
    public static defaultProps: Readonly<Partial<IExpandableProps>> = {
        expandKey: KeyCode.downArrow
    };

    private ignoreClick?: boolean;

    public state = { expanded: false };

    public render() {
        const { expanded } = this.state;

        const child: (props: IExpandableChildProps) => JSX.Element = this.props.children as any;
        return (
            <>
                {child({ expanded, onClick: this.onClick, onKeyDown: this.onKeyDown, onMouseDown: this.onMouseDown })}
                {expanded && this.props.renderCallout && this.props.renderCallout()}
            </>
        );
    }

    public collapse = () => {
        if (this.state.expanded) {
            this.setState({ expanded: false });
            if (this.props.onCollapse) {
                this.props.onCollapse();
            }
        }
    };

    public expand = () => {
        if (!this.state.expanded) {
            this.setState({ expanded: !this.state.expanded });
            if (this.props.onExpand) {
                this.props.onExpand();
            }
        }
    };

    private isExpandKey(event: React.KeyboardEvent<HTMLElement>) {
        return (Array.isArray(this.props.expandKey) && this.props.expandKey.indexOf(event.which) !== -1) || event.which === this.props.expandKey;
    }

    private onClick = (event: React.MouseEvent<HTMLElement>) => {
        if (!this.props.disabled) {
            if (!event.defaultPrevented) {
                if (!this.state.expanded && !this.ignoreClick) {
                    this.expand();
                    event.preventDefault();
                }
            }
        } else {
            event.preventDefault();
        }
    };

    private onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (!event.defaultPrevented) {
            // If the control key is pressed we want to navigate in a focus zone and not open the menu
            if (event.ctrlKey) {
                return;
            }

            if (!this.state.expanded && this.isExpandKey(event)) {
                this.ignoreClick = false;
                this.expand();
                event.preventDefault();
            }
        }
    };

    private onMouseDown = () => {
        // If the callout is expanded when we click on it, we want to ignore the click
        // and let the blur occur and close the callout.
        this.ignoreClick = this.state.expanded;
    };
}

let expandableContainerId = 1;

/**
 * ExpandableContainer is a specialized form of Expandable and generally shouldn't be used.
 * It's main goal is to provider mouse enter/leave behavior for collapsing.
 *
 * @NOTE: This component MAY be deprecated in the future, use <Expandable /> instead.
 */
export class ExpandableContainer extends React.Component<IExpandableContainerProps> implements IExpandable {
    public static contextType = FocusGroupContext;
    public static defaultProps: Readonly<Partial<IExpandableContainerProps>> = {
        collapseDelay: 250,
        expandDelay: 250,
        expandKey: [KeyCode.downArrow, KeyCode.enter]
    };

    private element = React.createRef<HTMLDivElement>();
    private expandedOnHover = false;
    private expandable = React.createRef<Expandable>();
    private expandableId = "expandableContainer-" + expandableContainerId++;

    public render() {
        return (
            <FocusZoneContext.Consumer>
                {zoneContext => (
                    <MouseWithin
                        enterDelay={this.props.expandDelay}
                        leaveDelay={this.props.collapseDelay}
                        onMouseEnter={this.onMouseEnter}
                        onMouseLeave={this.onMouseLeave}
                        updateStateOnMouseChange={false}
                    >
                        {(mouseContext: IMouseWithinStatus) => (
                            <Expandable {...this.props} renderCallout={undefined} ref={this.expandable}>
                                {(expandableProps: IExpandableChildProps) => (
                                    <div
                                        aria-controls={expandableProps.expanded ? getSafeId(this.props.expandableId) : undefined}
                                        aria-expanded={expandableProps.expanded}
                                        aria-haspopup={true}
                                        aria-label={this.props.ariaLabel}
                                        className={css(
                                            this.props.className,
                                            "bolt-expandable-container flex-row flex-center",
                                            expandableProps.expanded && "expanded"
                                        )}
                                        data-focuszone={!this.props.disabled && !this.props.excludeFocusZone ? zoneContext.focuszoneId : undefined}
                                        id={getSafeId(this.props.id)}
                                        onClick={expandableProps.onClick}
                                        onFocus={this.onFocus}
                                        onKeyDown={expandableProps.onKeyDown}
                                        onMouseDown={expandableProps.onMouseDown}
                                        onMouseEnter={mouseContext.onMouseEnter}
                                        onMouseLeave={mouseContext.onMouseLeave}
                                        role={this.props.role || "button"}
                                        tabIndex={getTabIndex(this.props, this.context)}
                                        ref={this.element}
                                    >
                                        {this.props.children}
                                        {expandableProps.expanded && this.renderCallout()}
                                    </div>
                                )}
                            </Expandable>
                        )}
                    </MouseWithin>
                )}
            </FocusZoneContext.Consumer>
        );
    }

    public collapse = () => {
        if (this.expandable.current) {
            this.expandable.current.collapse();
        }
    };

    public expand = () => {
        if (this.expandable.current) {
            this.expandable.current.expand();
        }
    };

    private onFocus = (event: React.FocusEvent<HTMLElement>) => {
        // Only call onFocus for the FocusGroup is the expandable itself is getting focus.
        // Otherwise it is going to a child element and it should manage focus appropriately.
        if (this.props.id && (!this.element.current || this.element.current === event.target)) {
            this.context.onFocus(this.props.id);
        }
    };

    private onMouseEnter = () => {
        if (this.props.expandOnHover) {
            this.expandedOnHover = true;
            setFocusVisible(false);
            this.expand();
        }
    };

    private onMouseLeave = () => {
        if (this.expandedOnHover) {
            this.collapse();
        }
    };

    private renderCallout = () => {
        return this.props.renderCallout(this.expandable.current!, this.expandableId, this.element.current!);
    };
}
